import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';

import { ReactComponent as CrossIcon } from '../../../assets/images/CrossIcon.svg';
import {
  EXPERIENCE_LEVEL_KEYS,
  INDUSTRY_KEYS,
  OPPORTUNITY_TYPE_KEYS,
  REMOTE_WORK_TYPE_KEYS,
  REQUIRED_EDUCATION_KEYS,
  SKILLS_KEYS,
  WORK_PLACE_TYPE_KEYS,
} from '../../../constants/commonConstants.js';
import { useJobFilterContext } from '../../../contexts/JobFilterProvider';
import { selectFilterDropdownValues, selectSkills } from '../../../selectors/filterSelectors';
import ButtonWithIcon from '../../shared/ButtonWithIcon/ButtonWithIcon';
import CustomCheckbox from '../../shared/customCheckbox/customCheckbox';
import CustomMultiSelect from '../../shared/CustomMultiSelect/CustomMultiSelect';
import Switch from '../../shared/Switch/Switch';

import styles from './FiltersDropdown.module.scss';

export const FilterDropdown = ({ setfilters, setIsDropdownVisible, inputRef }) => {
  const { t } = useTranslation('common');
  const { jobFilterValues, setJobFilterValues, getTotalSelectedCount } = useJobFilterContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const skillOptions = useSelector(selectSkills);
  const {
    functions: functionOptions,
    opportunityTypes: opportunityTypeOptions,
    industries: industryOptions,
    workTypes: workTypeOptions,
    experienceLevels: experienceLevelOptions,
    requiredEducation: educationOptions,
    remoteWork: remoteWorkOptions,
  } = useSelector(selectFilterDropdownValues);

  const getWorkPlaceTypeLabel = (name) =>
    WORK_PLACE_TYPE_KEYS[name] ? t(`workPlaceType.${WORK_PLACE_TYPE_KEYS[name]}`) : name;

  const getRemoteWorkLabel = (name) =>
    REMOTE_WORK_TYPE_KEYS[name] ? t(`remoteWork.${REMOTE_WORK_TYPE_KEYS[name]}`) : name;

  const getExperienceLevelLabel = (name) =>
    EXPERIENCE_LEVEL_KEYS[name] ? t(`experienceLevel.${EXPERIENCE_LEVEL_KEYS[name]}`) : name;

  const getRequiredEducationLabel = (name) =>
    REQUIRED_EDUCATION_KEYS[name] ? t(`requiredEducation.${REQUIRED_EDUCATION_KEYS[name]}`) : name;

  const getIndustryLabel = (name) => (INDUSTRY_KEYS[name] ? t(`industry.${INDUSTRY_KEYS[name]}`) : name);

  const getOpportunityTypeLabel = (name) =>
    OPPORTUNITY_TYPE_KEYS[name] ? t(`opportunityType.${OPPORTUNITY_TYPE_KEYS[name]}`) : name;

  const getSkillLabel = (name) => (SKILLS_KEYS[name] ? t(`skills.${SKILLS_KEYS[name]}`) : name);

  const isWorkPlaceType = (types) => types === 'work_place_types' || types === 'work_place_type';
  const isOpportunityType = (types) => types === 'opportunityType';

  const getOptionLabel = (option, types) => {
    if (isWorkPlaceType(types)) return getWorkPlaceTypeLabel(option.name);
    if (types === 'experience_level') return getExperienceLevelLabel(option.name);
    if (types === 'required_education') return getRequiredEducationLabel(option.name);
    if (types === 'industry') return getIndustryLabel(option.name);
    if (types === 'opportunityType') return getOpportunityTypeLabel(option.name);
    if (types === 'skills') return getSkillLabel(option.name);
    return option.name;
  };

  const selectOptions = (options, types = null) =>
    options.map((option) => ({
      label: getOptionLabel(option, types),
      value: isWorkPlaceType(types) || isOpportunityType(types) ? option.name : option.id,
    }));

  const selectTypeValues = (ids, typeOption, types = null) => {
    const options = types ? selectOptions(typeOption, types) : selectOptions(typeOption);
    const filteredOptions = options?.filter((option) => ids.includes(option.value));
    return filteredOptions;
  };

  const handleCheckboxChange = (event, field, setFieldValue, types = null) => {
    const { value } = event.target;
    const id = Number(value);
    const currentValues = field.value;

    let newValues;

    if (types) {
      newValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];
    } else {
      newValues = currentValues.includes(id) ? currentValues.filter((item) => item !== id) : [...currentValues, id];
    }

    setFieldValue(field.name, newValues);
  };

  const applyFilterHandler = (values) => {
    setJobFilterValues(values);
    setIsDropdownVisible(false);
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (Array.isArray(value)) {
        if (value.length > 0) {
          searchParams.set(key, value.join(','));
        } else {
          searchParams.delete(key);
        }
      } else if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    searchParams.set('id', '');
    setSearchParams(searchParams);
    setfilters((prevFilters) => ({
      ...prevFilters,
      filters: {
        industries: values.industries,
        function: values.functionValue,
        min_salary: values.minSalary,
        max_salary: values.maxSalary,
        work_types: values.opportunityType,
        experience: values.experienceLevel,
        education: values.requiredEducation,
        work_place_types: values.workType,
        urgency: values.remoteWork,
        skills: values.skills,
      },
    }));
  };

  const handleResetFilters = (setFieldValue) => {
    const resetValues = {
      functionValue: [],
      industries: [],
      opportunityType: [],
      workType: [],
      experienceLevel: [],
      skills: [],
      requiredEducation: [],
      remoteWork: [],
      minSalary: '',
      maxSalary: '',
      alert: false,
    };

    Object.entries(resetValues).forEach(([fieldName, value]) => {
      setFieldValue(fieldName, value);
    });

    applyFilterHandler(resetValues);
  };

  const handleClickOutside = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Formik initialValues={jobFilterValues} onSubmit={(values) => applyFilterHandler(values)}>
      {({ values, setFieldValue }) => (
        <Form className={styles.filterContainer}>
          <div className={styles.formContainer}>
            <div className={styles.filterHeader}>
              <div className={styles.filterHeadingCont}>
                <span className={styles.heading}>{t('filters.jobFilters')}</span>

                <button type="button" onClick={() => setIsDropdownVisible(false)} className={styles.iconButton}>
                  <CrossIcon />
                </button>
              </div>
              <div className={styles.filterAlert}>
                <Switch
                  checked={false}
                  onChange={(e) => setFieldValue('alert', e)}
                  onColor="#0FBC71"
                  offColor="#ccc"
                  onHandleColor="#fff"
                  offHandleColor="#fff"
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  height={20}
                  width={36}
                  borderRadius={12}
                  activeBoxShadow="0 0 2px 3px #4caf50"
                />
              </div>
            </div>
            <div className={styles.filterInputCont}>
              {functionOptions?.length > 0 ? (
                <div className={styles.fullWidthInputCont}>
                  <label className={styles.labelText} htmlFor="Function">
                    {t('filters.function')}
                  </label>
                  <CustomMultiSelect
                    options={selectOptions(functionOptions)}
                    value={selectTypeValues(values?.functionValue, functionOptions)}
                    onChange={(selectedOptions) => {
                      setFieldValue(
                        'functionValue',
                        selectedOptions.map((option) => option.value)
                      );
                    }}
                    placeholder={t('placeholders.function')}
                    isFilterDropdown
                    setIndicator
                  />
                </div>
              ) : null}
              <div className={styles.fullWidthInputCont}>
                <label className={styles.labelText} htmlFor="Industries">
                  {t('filters.industries')}
                </label>
                <CustomMultiSelect
                  className="filter-industries-select"
                  options={selectOptions(industryOptions, 'industry')}
                  value={selectTypeValues(values?.industries, industryOptions, 'industry')}
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      'industries',
                      selectedOptions.map((option) => option.value)
                    );
                  }}
                  placeholder={t('placeholders.industries')}
                  isFilterDropdown
                  setIndicator
                />
              </div>
              <div className={styles.fullWidthInputCont}>
                <label className={styles.labelText} htmlFor="Opportunity">
                  {t('filters.opportunityType')}
                </label>
                <CustomMultiSelect
                  placeholder={t('placeholders.selectOpportunityType')}
                  options={selectOptions(opportunityTypeOptions, 'opportunityType')}
                  value={selectTypeValues(values?.opportunityType, opportunityTypeOptions, 'opportunityType')}
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      'opportunityType',
                      selectedOptions.map((option) => option.value)
                    );
                  }}
                  isFilterDropdown
                  setIndicator
                />
              </div>
              <div className={styles.filterLocationFunction}>
                <div className={styles.locationCont}>
                  <label className={styles.labelText} htmlFor="minsalary">
                    {t('filters.minSalary')}
                  </label>
                  <input
                    className={styles.inputField}
                    min="0"
                    value={values.minSalary}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue >= 0) {
                        setFieldValue('minSalary', e.target.value);
                      }
                    }}
                    placeholder={t('placeholders.enterMinSalary')}
                  />
                </div>
                <div className={styles.locationCont}>
                  <label className={styles.labelText} htmlFor="maxSalary">
                    {t('filters.maxSalary')}
                  </label>
                  <input
                    className={styles.inputField}
                    min="0"
                    value={values.maxSalary}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue >= 0) {
                        setFieldValue('maxSalary', e.target.value);
                      }
                    }}
                    placeholder={t('placeholders.enterMaxSalary')}
                  />
                </div>
              </div>
              <div className={styles.checkboxCont}>
                <label className={styles.checkboxHeading} htmlFor="Work-type">
                  {t('filters.workPlaceType')}
                </label>
                <div className={styles.checkboxGridCont}>
                  {workTypeOptions.map((type) => (
                    <div key={type.name} className={styles.checkboxTextCont}>
                      <CustomCheckbox
                        type={getWorkPlaceTypeLabel(type.name)}
                        id={type.name}
                        checked={values.workType.includes(type.name)}
                        onCheckboxChange={(e) =>
                          handleCheckboxChange(
                            e,
                            { value: values.workType, name: 'workType' },
                            setFieldValue,
                            'workPlaceType'
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.checkboxCont}>
                <label className={styles.checkboxHeading} htmlFor="Experience">
                  {t('filters.experienceLevel')}
                </label>
                <div className={styles.checkboxGridCont}>
                  {experienceLevelOptions.map((level) => (
                    <div key={level.id} className={styles.checkboxTextCont}>
                      <CustomCheckbox
                        type={getExperienceLevelLabel(level.name)}
                        id={level.id}
                        checked={values.experienceLevel.includes(level.id)}
                        onCheckboxChange={(e) =>
                          handleCheckboxChange(
                            e,
                            { value: values.experienceLevel, name: 'experienceLevel' },
                            setFieldValue
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fullWidthInputCont}>
                <label className={styles.labelText} htmlFor="JobSkills">
                  {t('filters.skills')}
                </label>
                <CustomMultiSelect
                  className="filter-skills-select"
                  options={selectOptions(skillOptions, 'skills')}
                  value={selectTypeValues(values?.skills, skillOptions, 'skills')}
                  onChange={(selectedOptions) => {
                    setFieldValue(
                      'skills',
                      selectedOptions.map((option) => option.value)
                    );
                  }}
                  placeholder={t('placeholders.skills')}
                  isFilterDropdown
                  setIndicator
                />
              </div>
              <div className={styles.checkboxCont}>
                <label className={styles.checkboxHeading} htmlFor="required-education">
                  {t('filters.requiredEducation')}
                </label>
                <div className={styles.checkboxGridCont}>
                  {educationOptions.map((education) => (
                    <div key={education.id} className={styles.checkboxTextCont}>
                      <CustomCheckbox
                        type={getRequiredEducationLabel(education.name)}
                        id={education.id}
                        checked={values.requiredEducation.includes(education.id)}
                        onCheckboxChange={(e) =>
                          handleCheckboxChange(
                            e,
                            { value: values.requiredEducation, name: 'requiredEducation' },
                            setFieldValue
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.checkboxCont}>
                <label className={styles.checkboxHeading} htmlFor="remote-work">
                  {t('filters.urgency')}
                </label>
                <div className={styles.checkboxGridCont}>
                  {remoteWorkOptions.map((work) => (
                    <div key={work.id} className={styles.checkboxTextCont}>
                      <CustomCheckbox
                        type={getRemoteWorkLabel(work.name)}
                        id={work.id}
                        checked={values.remoteWork.includes(work.id)}
                        onCheckboxChange={(e) =>
                          handleCheckboxChange(e, { value: values.remoteWork, name: 'remoteWork' }, setFieldValue)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.applyBtn}>
            <div className={styles.leftButtons}>
              <ButtonWithIcon
                text={t('filterButtons.applyFilters')}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-primary-40"
                variant="secondary-36"
                type="submit"
              />
              <ButtonWithIcon
                text={t('cancel')}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-tertiary-40"
                variant="tertiary-36"
                onClick={() => setIsDropdownVisible(false)}
              />
            </div>
            {getTotalSelectedCount(values) > 0 && (
              <ButtonWithIcon
                text={t('filterButtons.reset')}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-tertiary-40"
                variant="tertiary-36"
                onClick={() => handleResetFilters(setFieldValue)}
              />
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default FilterDropdown;
